import * as React from 'react'
import { Switch } from '@/components/ui/switch'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Badge } from '@/components/ui/badge'
import { RotateCcw, AlertCircle } from 'lucide-react'
import { AssetClass } from '@/types/api'
import { getAssetClassTheme, getAssetClassLabel } from '@/types/assets'
import { cn } from '@/lib/utils'

interface ClassificationToggleProps {
  /** Current classification value */
  value: AssetClass
  /** Whether this is a user override */
  isOverride: boolean
  /** Rules-based default classification */
  rulesBasedClass: AssetClass
  /** Callback when classification changes */
  onChange: (value: AssetClass, isOverride: boolean) => void
  /** Callback to reset to default */
  onReset?: () => void
  /** Whether this asset has positions (for warning) */
  hasPositions?: boolean
  /** Disable interactions */
  disabled?: boolean
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * ClassificationToggle Component
 *
 * A toggle control for switching between CRYPTO and TRADFI classifications.
 * Features:
 * - Visual distinction between CRYPTO (purple) and TRADFI (emerald)
 * - Override indicator with reset capability
 * - Warning for assets with positions
 * - Accessible keyboard navigation
 *
 * @example
 * ```tsx
 * <ClassificationToggle
 *   value="CRYPTO"
 *   isOverride={true}
 *   rulesBasedClass="TRADFI"
 *   onChange={(val, isOverride) => console.log(val, isOverride)}
 *   onReset={() => console.log('reset')}
 *   hasPositions={true}
 * />
 * ```
 */
export function ClassificationToggle({
  value,
  isOverride,
  rulesBasedClass,
  onChange,
  onReset,
  hasPositions = false,
  disabled = false,
  size = 'md',
}: ClassificationToggleProps) {
  const theme = getAssetClassTheme(value)
  const isCrypto = value === 'CRYPTO'

  const handleToggle = () => {
    if (disabled) return
    const newValue: AssetClass = isCrypto ? 'TRADFI' : 'CRYPTO'
    onChange(newValue, true) // User action = override
  }

  const handleReset = (e: React.MouseEvent) => {
    e.stopPropagation()
    onReset?.()
  }

  // Size variants
  const sizeClasses = {
    sm: {
      container: 'h-6',
      label: 'text-xs',
      badge: 'text-[10px] px-1 py-0',
      switch: 'scale-75',
      reset: 'w-4 h-4',
    },
    md: {
      container: 'h-8',
      label: 'text-sm',
      badge: 'text-xs px-1.5 py-0.5',
      switch: 'scale-90',
      reset: 'w-5 h-5',
    },
    lg: {
      container: 'h-10',
      label: 'text-base',
      badge: 'text-xs px-2 py-1',
      switch: 'scale-100',
      reset: 'w-5 h-5',
    },
  }[size]

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex items-center gap-2 rounded-lg border px-2 transition-colors',
          theme.bg,
          theme.border,
          sizeClasses.container,
          disabled && 'opacity-50 cursor-not-allowed'
        )}
        aria-label={`Asset classification: ${getAssetClassLabel(value)}`}
      >
        {/* Asset class label */}
        <span
          className={cn(
            'font-medium whitespace-nowrap',
            theme.text,
            sizeClasses.label
          )}
        >
          {getAssetClassLabel(value)}
        </span>

        {/* Toggle switch */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={sizeClasses.switch}>
              <Switch
                checked={isCrypto}
                onCheckedChange={handleToggle}
                disabled={disabled}
                aria-label={`Toggle to ${isCrypto ? 'TradFi' : 'Crypto'}`}
                className={cn(
                  'data-[state=checked]:bg-purple-500 data-[state=unchecked]:bg-emerald-500'
                )}
              />
            </div>
          </TooltipTrigger>
          {hasPositions && (
            <TooltipContent side="top" className="max-w-xs">
              <p className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                This asset has portfolio positions. Classification changes will
                affect scenario calculations.
              </p>
            </TooltipContent>
          )}
        </Tooltip>

        {/* Override indicator */}
        {isOverride && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge
                variant="outline"
                className={cn(
                  'bg-amber-50 text-amber-700 border-amber-200 cursor-pointer hover:bg-amber-100',
                  sizeClasses.badge
                )}
                onClick={handleReset}
              >
                <span className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  Modified
                </span>
              </Badge>
            </TooltipTrigger>
            <TooltipContent side="top">
              <div className="space-y-1">
                <p className="font-medium">Classification Overridden</p>
                <p className="text-sm text-muted-foreground">
                  Rules-based: {getAssetClassLabel(rulesBasedClass)}
                </p>
                <p className="text-xs text-muted-foreground">
                  Click to reset to default
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Reset button (visible when overridden) */}
        {isOverride && onReset && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleReset}
                className={cn(
                  'flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors',
                  sizeClasses.reset
                )}
                aria-label="Reset to rules-based default"
              >
                <RotateCcw className="w-3 h-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>Reset to {getAssetClassLabel(rulesBasedClass)}</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  )
}
